import { ArrowPosition } from "../../StickyNote";
import { AnchorIds, TourIds, TourSignal, TourStep, WriteRewriteTourVariations } from "../../TourTypes";
import {
  AgenticChatStickyContent,
  AgenticToolsStickyContent,
  IntroStickyContent,
  WriteStickyContent,
  WriteRewriteStoryBibleContent,
} from "./Context";

const getFirstStepByVariation = (variation: WriteRewriteTourVariations): TourStep => {
  switch (variation) {
    case WriteRewriteTourVariations.ONBOARDING_NEW_BOOK:
    case WriteRewriteTourVariations.ONBOARDING_IMPORTED_PROJECT:
    case WriteRewriteTourVariations.BRAINDUMP_NOT_SURE:
      return {
        id: "1",
        stickies: [
          {
            content: "👋 Hello! Ready to learn how to use Sudowrite? Click this project to get started.",
            offsetX: 200,
            offsetY: 0,
            anchorId: AnchorIds.PROJECT_CARD,
            rotation: 2,
            arrowPosition: ArrowPosition.BottomLeft,
          },
        ],
        listeningFor: [TourSignal.PROJECT_CARD_CLICKED],
      };

    case WriteRewriteTourVariations.FROM_HELP_MENU:
      return {
        id: "1",
        stickies: [
          {
            content: (
              <p>
                👋 Welcome to the quickstart tour! Click on <strong>any project</strong> to get started.
              </p>
            ),
            offsetX: -380,
            offsetY: -25,
            anchorId: AnchorIds.LOGO,
            rotation: -5,
            arrowPosition: ArrowPosition.Bottom,
          },
        ],
        listeningFor: [TourSignal.PROJECT_CARD_CLICKED],
      };
    default:
      throw new Error(`Unknown variation: ${variation}`);
  }
};

const getWriteRewriteTour = (variation: WriteRewriteTourVariations) => {
  const firstStep = getFirstStepByVariation(variation);

  return {
    id: TourIds.WRITE_REWRITE,
    variation,
    steps: [
      firstStep,
      {
        id: "1.1",
        stickies: [
          {
            content: <AgenticChatStickyContent />,
            offsetX: -250,
            offsetY: 130,
            anchorId: AnchorIds.RIGHT_BAR,
            arrowPosition: ArrowPosition.TopRight,
            rotation: 3,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
      {
        id: "1.2",
        stickies: [
          {
            content: <AgenticToolsStickyContent />,
            offsetX: 50,
            offsetY: 35,
            anchorId: AnchorIds.WRITE_BUTTON,
            arrowPosition: ArrowPosition.TopLeft,
            rotation: -2,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
      {
        id: "2",
        stickies: [
          {
            content: <WriteStickyContent />,
            offsetX: 50,
            offsetY: 25,
            anchorId: AnchorIds.WRITE_BUTTON,
            arrowPosition: ArrowPosition.TopLeft,
          },
          {
            content: <IntroStickyContent variation={variation} />,
            offsetX: 31,
            offsetY: variation === WriteRewriteTourVariations.FROM_HELP_MENU ? 200 : 225,
            anchorId: AnchorIds.WRITE_BUTTON,
            rotation: -5,
          },
        ],
        listeningFor: [TourSignal.WRITE_BUTTON_CLICKED],
      },
      {
        id: "3",
        stickies: [
          {
            content: (
              <p>
                🎉 Way to go! <strong>Write</strong> looks at what you've written and keeps going. It's great for getting inspiration when you're blocked.
              </p>
            ),
            offsetX: 0,
            offsetY: 50,
            anchorId: AnchorIds.WRITE_BUTTON,
            rotation: -3,
          },
        ],
        listeningFor: [TourSignal.SET_DONE_STREAMING],
      },
      {
        id: "3.1",
        stickies: [
          {
            content: (
              <p>
                Pick one of the written suggestions and click <strong>Insert</strong>.
              </p>
            ),
            offsetX: -250,
            offsetY: 275,
            anchorId: AnchorIds.RIGHT_BAR,
            arrowPosition: ArrowPosition.BottomRight,
            rotation: 5,
          },
        ],
        listeningFor: [TourSignal.INSERT_CLICKED],
      },
      {
        id: "4",
        stickies: [
          {
            content: (
              <>
                <p>Nice job! 😎</p>
                <p>Anything Sudowrite writes is purple (until you edit it) to remind you that it's still a rough draft.</p>
              </>
            ),
            offsetX: 65,
            offsetY: 80,
            anchorId: AnchorIds.WRITE_BUTTON,
            rotation: -5,
          },
          {
            content: (
              <p>
                Let's use <strong>Rewrite</strong> to refine the tone of this section.
              </p>
            ),
            offsetX: -100,
            offsetY: -270,
            anchorId: AnchorIds.HIGHLIGHT_MENU_REWRITE_BUTTON,
            arrowPosition: ArrowPosition.Bottom,
          },
          {
            content: (
              <p>
                Select some text to bring up the <strong>highlight menu</strong>.
              </p>
            ),
            offsetX: 65,
            offsetY: 330,
            anchorId: AnchorIds.WRITE_BUTTON,
            renderIfAnchorMissing: AnchorIds.HIGHLIGHT_MENU_REWRITE_BUTTON,
            arrowPosition: ArrowPosition.BottomRight,
            rotation: 2,
          },
        ],
        listeningFor: [TourSignal.HIGHLIGHT_MENU_REWRITE_BUTTON_CLICKED],
      },
      {
        id: "5",
        stickies: [
          {
            content: (
              <p>
                Pick a <strong>Rewrite</strong> style from the drop-down, then click <strong>Go</strong>!
              </p>
            ),
            offsetX: -250,
            offsetY: 0,
            anchorId: AnchorIds.REWRITE_MENU_DROPDOWN,
            arrowPosition: ArrowPosition.TopRight,
            rotation: -5,
          },
        ],
        listeningFor: [TourSignal.REWRITE_GO_CLICKED],
      },
      {
        id: "6",
        stickies: [
          {
            content: (
              <p>
                You can also use <strong>Rewrite</strong> up here! 🙂
              </p>
            ),
            offsetX: -60,
            offsetY: 40,
            anchorId: AnchorIds.TOP_TOOLBAR_REWRITE_BUTTON,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.SET_DONE_STREAMING],
      },
      {
        id: "7",
        stickies: [
          {
            content: (
              <p>
                Well done! Pick one of the written suggestions and <strong>Insert</strong> to update.
              </p>
            ),
            offsetX: -250,
            offsetY: 260,
            anchorId: AnchorIds.RIGHT_BAR,
            arrowPosition: ArrowPosition.BottomRight,
            rotation: -5,
          },
        ],
        listeningFor: [TourSignal.INSERT_CLICKED],
      },
      {
        id: "8",
        stickies: [
          {
            content: "🎉 Amazing! That's just a taste of what Sudowrite offers, but it's enough to get you started.",
            offsetX: 100,
            offsetY: 220,
            anchorId: AnchorIds.LEFT_BAR,
            rotation: -5,
          },
          {
            content: <WriteRewriteStoryBibleContent />,
            offsetX: -200,
            offsetY: -300,
            rotation: 2,
            anchorId: AnchorIds.RIGHT_BAR_BOTTOM,
          },
          {
            content: "Need more help? Click here for tutorials and support!",
            offsetX: -250,
            offsetY: 20,
            anchorId: AnchorIds.HELP_ICON,
            arrowPosition: ArrowPosition.TopRight,
            rotation: -5,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
    ],
  };
};

export { getWriteRewriteTour };
